import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { fetchTraceGraphByInternalOrderId } from '@/api/orders';
import { OrderTracingTab } from '@/app/(client)/orders/-components/OrderTracingTab';
import { useEntityScreeningMatches } from '@/app/(client)/orders/-hooks/useEntityScreeningMatches';
import {
  internalOrderTraceGraphKeys,
  useTraceGraphByInternalOrderId,
} from '@/app/(client)/orders/-hooks/useTraceGraphByInternalOrderId';
import { Skeleton } from '@/components/ui/skeleton';
import { useCanAccessEntityScreening } from '@/hooks/useCanAccessEntityScreening';
import { useFeatures } from '@/hooks/useFeatures';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/(detail)/internal-orders/$ioId/tracing',
)({
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: internalOrderTraceGraphKeys.detail(params.ioId),
      queryFn: () =>
        fetchTraceGraphByInternalOrderId(params.ioId).then(res => res.data),
    }),
  component: InternalOrderTracingRoute,
});

function InternalOrderTracingRoute() {
  const { ioId } = Route.useParams();
  const { isTracingAiEnabled, isEntityScreeningV2Enabled } = useFeatures();
  // `isKharonV2Allowed`, not the KHARON_V2 platform feature — see below.
  const canAccessEntityScreening = useCanAccessEntityScreening();

  const { data: traceGraph, isLoading } = useTraceGraphByInternalOrderId(ioId);

  const companyIds = useMemo(
    () =>
      traceGraph?.layers?.flatMap(
        layer => layer.nodes?.map(node => node.assetId) ?? [],
      ) ?? [],
    [traceGraph],
  );

  const { data: screeningMatches } = useEntityScreeningMatches(companyIds, {
    // The old dashboard gates the screening flag on
    // `isFeatureEnabled('entity-screening-v2') && canAccessKharonV2`, and
    // `canAccessKharonV2` is the shared `isKharonV2Allowed(companyId, userId)`
    // contract — true everywhere except one carved-out company. This gated on
    // the KHARON_V2 *platform feature* instead, which is a different thing, so
    // any company without that feature lost a compliance signal the old
    // dashboard still showed (BI-1655). Supply-chain mapping already gates the
    // same badge the correct way.
    enabled: isEntityScreeningV2Enabled && canAccessEntityScreening,
  });

  if (isLoading) {
    return (
      <div className='flex flex-col gap-4'>
        <Skeleton className='h-8 w-40' />
        <Skeleton className='w-full rounded-xl h-[calc(100vh-340px)] min-h-100' />
      </div>
    );
  }

  if (!traceGraph) return null;

  return (
    <OrderTracingTab
      orderId={ioId}
      traceGraph={traceGraph}
      entityScreeningMatches={screeningMatches ?? []}
      isTracingAiEnabled={isTracingAiEnabled}
    />
  );
}
